import { DomainValidationError } from "./identifiers.js";
// The window of one analytics read, resolved on the server against the clock of the gate (ADR 0017, decision 7). The browser sends a range name
// (and, for a zoom, a `from` and `to` inside it). It never sends the end of "now".
export const ANALYTICS_WINDOW_RANGES = ["24h", "7d", "30d", "90d", "12m", "all"];
/** App events and visits are kept 13 months (owner decision 3). Records are kept as the retention matrix says, so the Overview reads them in full. */
export const ANALYTICS_RETENTION_MONTHS = 13;
/** The zone the Overview is cut in when the browser sends none (open point Q9). The schema package holds the same value. */
export const ANALYTICS_DEFAULT_TIME_ZONE = "Asia/Kolkata";
/** The shortest window a zoom may ask for. */
export const ANALYTICS_MIN_WINDOW_MS = 3_600_000;
/** A parameter of an analytics read is not allowed. The route answers `400`. The code is closed and carries no value. */
export class AnalyticsParameterError extends DomainValidationError {
    code;
    constructor(code) {
        super(code, "is not allowed");
        this.name = "AnalyticsParameterError";
        this.code = code;
    }
}
const HOUR_MS = 3_600_000;
const DAY_MS = 24 * HOUR_MS;
const RANGE_SPAN_MS = {
    "24h": DAY_MS,
    "7d": 7 * DAY_MS,
    "30d": 30 * DAY_MS,
    "90d": 90 * DAY_MS,
};
/** `date` minus whole calendar months in UTC. The day is kept, or the last day of a shorter month (31 March minus one month is 28 or 29 February). */
export function subtractMonthsUtc(date, months) {
    const total = date.getUTCFullYear() * 12 + date.getUTCMonth() - months;
    const year = Math.floor(total / 12);
    const month = total - year * 12;
    const lastDay = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
    const result = new Date(date.getTime());
    result.setUTCFullYear(year, month, Math.min(date.getUTCDate(), lastDay));
    return result;
}
const invalid = () => new AnalyticsParameterError("analytics.window_invalid");
/**
 * Resolves the window. `now` is the clock of the gate. `customerCreatedAt` is where `all` starts for records. `zoom` is the drag-to-zoom
 * window: both ends or none. A zoom must lie inside the resolved range, end after it starts, and last at least one hour; otherwise the
 * answer is `analytics.window_invalid`.
 */
export function resolveAnalyticsWindow(input) {
    const { range, now } = input;
    const retentionCutoff = subtractMonthsUtc(now, ANALYTICS_RETENTION_MONTHS);
    const span = RANGE_SPAN_MS[range];
    let rangeFrom;
    if (span !== undefined)
        rangeFrom = new Date(now.getTime() - span);
    else if (range === "12m")
        rangeFrom = subtractMonthsUtc(now, 12);
    else
        rangeFrom = new Date(Math.min(input.customerCreatedAt.getTime(), now.getTime() - HOUR_MS));
    let from = rangeFrom;
    let to = now;
    if (input.zoom !== undefined) {
        from = input.zoom.from;
        to = input.zoom.to;
        const insideRange = from.getTime() >= rangeFrom.getTime() && to.getTime() <= now.getTime();
        const longEnough = to.getTime() - from.getTime() >= ANALYTICS_MIN_WINDOW_MS;
        if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime()) || !insideRange || !longEnough) {
            throw invalid();
        }
    }
    const appDataFrom = from.getTime() >= retentionCutoff.getTime() ? from : retentionCutoff;
    return { range, from, to, appDataFrom, retentionCutoff };
}
